import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import styles from './ClampedText.module.css'

type ClampedTextProps = {
  children: ReactNode
  className?: string
  lines?: number
}

export const ClampedText = ({ children, className = '', lines = 3 }: ClampedTextProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [clamped, setClamped] = useState(false)

  const checkClamp = useCallback(() => {
    const el = ref.current
    if (!el || expanded) return
    setClamped(el.scrollHeight > el.clientHeight + 1)
  }, [expanded])

  useEffect(() => {
    checkClamp()
    window.addEventListener('resize', checkClamp)
    return () => window.removeEventListener('resize', checkClamp)
  }, [checkClamp])

  useEffect(() => {
    checkClamp()
  }, [children, checkClamp])

  const style: Record<string, string> = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }

  if (!expanded) {
    style.WebkitLineClamp = String(lines)
  }

  const interactive = clamped || expanded
  const toggleExpanded = () => {
    if (interactive) {
      setExpanded((value) => !value)
    }
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setExpanded((value) => !value)
    }
  }

  return (
    <div
      className={[clamped ? styles.clamped : '', className].filter(Boolean).join(' ')}
      onClick={toggleExpanded}
      onKeyDown={handleKeyDown}
      ref={ref}
      role={interactive ? 'button' : undefined}
      style={style}
      tabIndex={interactive ? 0 : undefined}
      title={clamped || expanded ? (expanded ? 'Click to collapse' : 'Click to expand') : undefined}
    >
      {children}
    </div>
  )
}
